import React, { useEffect, useMemo, useState } from 'react';
import { INDIA_STATES, COUNTRIES } from '../data/geo';
import SearchableSelect from './SearchableSelect';

const AUTOSAVE_KEY = 'modularoom_feasibility_draft_v1';

const BUSINESS_TYPES = ['Airbnb', 'Boutique Resort', 'Farm Stay', 'Glamping', 'Weekend Retreat', 'Eco Resort', 'Holiday Homes', 'Other'];
const UTILITIES = ['Electricity', 'Water', 'Road Access', 'Sewerage', 'Internet', 'None yet'];
const TIMELINES = ['Immediately', 'Within 3 months', 'Within 6 months', 'Within 12 months', 'Just exploring'];

// --------- EMI helper ---------
export function calcEMI({ finance_amount, interest_rate, finance_tenure_years }) {
  const P = Number(finance_amount || 0);
  const r = Number(interest_rate || 0) / 100 / 12;
  const n = Number(finance_tenure_years || 0) * 12;
  if (P <= 0 || r <= 0 || n <= 0) return 0;
  const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Number.isFinite(emi) ? emi : 0;
}

const DEFAULT = {
  land_ownership: '',
  location_scope: '',
  location_country: 'India',
  location_state: '',
  location_city: '',
  business_type: '',
  business_type_other: '',
  suites_count: 5,
  financing_plan: '',
  down_payment_pct: 20,
  finance_amount: 0,
  interest_rate: 10.5,
  finance_tenure_years: 10,
  utilities: [],
  timeline: '',
  full_name: '',
  email: '',
  phone: '',
};

const StepDot = ({ active, done, testid }) => (
  <div
    data-testid={testid}
    className={`h-[3px] rounded-full flex-1 transition-colors duration-500 ${
      active ? 'bg-[#C7A36A]' : done ? 'bg-[#111]' : 'bg-black/10'
    }`}
  />
);

const OptCard = ({ selected, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-5 border transition-all duration-300 ${
      selected ? 'border-[#111] bg-white shadow-[0_16px_36px_-24px_rgba(0,0,0,0.35)]' : 'border-black/10 bg-white hover:border-black/40'
    }`}
  >
    <div className="flex items-start gap-3">
      <span className={`mt-1 w-3.5 h-3.5 rounded-full border ${selected ? 'bg-[#C7A36A] border-[#C7A36A]' : 'border-black/25'}`} />
      <span className="text-[15px] leading-relaxed">{children}</span>
    </div>
  </button>
);

const Field = ({ label, sub, children }) => (
  <label className="block">
    <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-2">{label}</div>
    {children}
    {sub && <div className="mt-2 text-[12px] text-black/50">{sub}</div>}
  </label>
);

const NumInput = ({ value, onChange, testid, prefix, suffix, step = 1, min, max }) => (
  <div className="flex items-center border-b border-black/15 focus-within:border-[#C7A36A]">
    {prefix && <span className="pr-2 text-black/50 text-[15px]">{prefix}</span>}
    <input
      type="number"
      data-testid={testid}
      value={Number.isFinite(value) ? value : ''}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      step={step}
      min={min}
      max={max}
      inputMode="decimal"
      className="w-full bg-transparent py-3 text-[16px] tracking-tight focus:outline-none tabular-nums"
    />
    {suffix && <span className="pl-2 text-black/50 text-[13px]">{suffix}</span>}
  </div>
);

const TextInput = ({ value, onChange, testid, placeholder, type = 'text' }) => (
  <input
    type={type}
    data-testid={testid}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-transparent border-b border-black/15 focus:border-[#C7A36A] focus:outline-none py-3 text-[16px]"
  />
);

export default function FeasibilityQuestionnaire({ onComplete }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(AUTOSAVE_KEY);
      return saved ? { ...DEFAULT, ...JSON.parse(saved) } : DEFAULT;
    } catch {
      return DEFAULT;
    }
  });
  const total = 8;

  // Autosave to localStorage
  useEffect(() => {
    try { localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(data)); } catch (e) { /* ignore quota */ }
  }, [data]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const emi = useMemo(() => calcEMI(data), [data]);

  // ---------- validation per step ----------
  const canProceed = useMemo(() => {
    switch (step) {
      case 1: return !!data.land_ownership;
      case 2: {
        if (!data.location_scope) return false;
        if (data.location_scope === 'india') return !!data.location_state && !!(data.location_city || '').trim();
        return !!data.location_country && !!(data.location_city || '').trim();
      }
      case 3: return !!data.business_type && (data.business_type !== 'Other' || (data.business_type_other || '').trim().length >= 2);
      case 4: return Number(data.suites_count) >= 1;
      case 5: {
        if (!data.financing_plan) return false;
        if (data.financing_plan === 'self') return true;
        return Number(data.finance_amount) > 0 && Number(data.interest_rate) > 0 && Number(data.finance_tenure_years) > 0;
      }
      case 6: return true; // utilities optional
      case 7: return !!data.timeline;
      case 8: return (data.full_name || '').trim().length >= 2 && /.+@.+\..+/.test(data.email || '') && (data.phone || '').trim().length >= 4;
      default: return false;
    }
  }, [step, data]);

  const next = () => {
    if (!canProceed) return;
    if (step === total) {
      onComplete({ ...data, emi_monthly: emi });
    } else {
      setStep((s) => s + 1);
    }
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="max-w-3xl mx-auto" data-testid="feasibility-questionnaire">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-[10px] tracking-[0.32em] uppercase text-black/45 mb-3">
          <span>Step {step} of {total}</span>
          <span>{Math.round((step / total) * 100)}%</span>
        </div>
        <div className="flex gap-1.5" data-testid="progress-bar">
          {Array.from({ length: total }).map((_, i) => (
            <StepDot key={i} active={i + 1 === step} done={i + 1 < step} testid={`step-dot-${i + 1}`} />
          ))}
        </div>
      </div>

      <div className="bg-white border border-black/10 p-8 md:p-12">
        {step === 1 && (
          <StepBlock title="Do you already own the land?" testid="q-land">
            <div className="grid gap-3">
              {[
                ['owned', 'Yes, I own the land'],
                ['lease', 'No, I plan to lease the land'],
                ['looking', "I'm still looking for land"],
              ].map(([k, l]) => (
                <OptCard key={k} selected={data.land_ownership === k} onClick={() => set({ land_ownership: k })} testid={`q1-${k}`}>{l}</OptCard>
              ))}
            </div>
          </StepBlock>
        )}

        {step === 2 && (
          <StepBlock title="Where is your project located?" testid="q-location">
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[['india', 'India'], ['outside', 'Outside India']].map(([k, l]) => (
                <OptCard key={k} selected={data.location_scope === k} onClick={() => set({ location_scope: k, location_state: '', location_country: k === 'india' ? 'India' : '' })} testid={`q2-${k}`}>{l}</OptCard>
              ))}
            </div>
            {data.location_scope === 'india' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="State / Union Territory">
                  <SearchableSelect value={data.location_state} onChange={(v) => set({ location_state: v })} options={INDIA_STATES} placeholder="Select state…" testid="q2-state" ariaLabel="Select state or union territory" />
                </Field>
                <Field label="City / Town">
                  <TextInput value={data.location_city} onChange={(v) => set({ location_city: v })} testid="q2-city" placeholder="e.g. Udaipur" />
                </Field>
              </div>
            )}
            {data.location_scope === 'outside' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Country">
                  <SearchableSelect value={data.location_country} onChange={(v) => set({ location_country: v })} options={COUNTRIES} placeholder="Select country…" testid="q2-country" ariaLabel="Select country" />
                </Field>
                <Field label="City / Town">
                  <TextInput value={data.location_city} onChange={(v) => set({ location_city: v })} testid="q2-city" placeholder="e.g. Bali" />
                </Field>
              </div>
            )}
          </StepBlock>
        )}

        {step === 3 && (
          <StepBlock title="What type of hospitality business are you planning?" testid="q-business">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {BUSINESS_TYPES.map((b) => (
                <OptCard key={b} selected={data.business_type === b} onClick={() => set({ business_type: b })} testid={`q3-${b.toLowerCase().replace(/\W+/g, '-')}`}>{b}</OptCard>
              ))}
            </div>
            {data.business_type === 'Other' && (
              <div className="mt-6">
                <Field label="Please describe">
                  <TextInput value={data.business_type_other} onChange={(v) => set({ business_type_other: v })} testid="q3-other-text" placeholder="Tell us about your business idea" />
                </Field>
              </div>
            )}
          </StepBlock>
        )}

        {step === 4 && (
          <StepBlock title="How many MODULAROOM suites are you planning?" testid="q-suites">
            <div className="flex items-baseline gap-6 mb-6">
              <div className="font-display text-6xl tracking-tight tabular-nums" data-testid="suites-count">
                {data.suites_count >= 20 ? '20+' : data.suites_count}
              </div>
              <div className="text-[13px] text-black/55">units</div>
            </div>
            <input
              type="range" min="1" max="20" step="1"
              value={Math.min(20, data.suites_count || 1)}
              onChange={(e) => set({ suites_count: Number(e.target.value) })}
              className="w-full accent-[#C7A36A]"
              data-testid="q4-slider"
            />
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => set({ suites_count: 25 })}
                data-testid="q4-20plus"
                className={`rounded-full px-5 py-2 text-[11px] tracking-[0.22em] uppercase border ${
                  data.suites_count >= 20 ? 'bg-[#111] text-white border-[#111]' : 'border-black/15 hover:border-[#C7A36A]'
                }`}
              >20+ units</button>
              <span className="text-[12px] text-black/50">For 20+ units, our team will prepare a bespoke plan.</span>
            </div>
          </StepBlock>
        )}

        {step === 5 && (
          <StepBlock title="How do you plan to finance your project?" testid="q-finance">
            <div className="grid gap-3">
              {[['self', 'Self-funded'], ['finance', 'Finance'], ['mixed', 'Combination of both']].map(([k, l]) => (
                <OptCard key={k} selected={data.financing_plan === k} onClick={() => set({ financing_plan: k })} testid={`q5-${k}`}>{l}</OptCard>
              ))}
            </div>
            {(data.financing_plan === 'finance' || data.financing_plan === 'mixed') && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Down payment"><NumInput value={data.down_payment_pct} onChange={(v) => set({ down_payment_pct: v })} testid="q5-down" suffix="%" step={1} min={0} max={100} /></Field>
                <Field label="Finance amount"><NumInput value={data.finance_amount} onChange={(v) => set({ finance_amount: v })} testid="q5-amount" prefix="₹" step={100000} /></Field>
                <Field label="Interest rate"><NumInput value={data.interest_rate} onChange={(v) => set({ interest_rate: v })} testid="q5-rate" suffix="%" step={0.1} /></Field>
                <Field label="Tenure"><NumInput value={data.finance_tenure_years} onChange={(v) => set({ finance_tenure_years: v })} testid="q5-tenure" suffix="years" step={1} min={1} max={30} /></Field>
                <div className="sm:col-span-2 p-5 bg-[#F5F5F5]">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-black/45">Estimated EMI</div>
                  <div className="mt-2 font-display text-3xl tracking-tight tabular-nums" data-testid="q5-emi">₹{Math.round(emi).toLocaleString('en-IN')} <span className="text-[13px] text-black/50 font-normal">/ month</span></div>
                </div>
              </div>
            )}
          </StepBlock>
        )}

        {step === 6 && (
          <StepBlock title="Which utilities are already available?" testid="q-utilities">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {UTILITIES.map((u) => {
                const on = (data.utilities || []).includes(u);
                return (
                  <button
                    type="button"
                    key={u}
                    onClick={() => set({
                      utilities: on
                        ? (data.utilities || []).filter((x) => x !== u)
                        : ([...(data.utilities || []), u].filter((x) => u === 'None yet' ? x === 'None yet' : x !== 'None yet')),
                    })}
                    data-testid={`q6-${u.toLowerCase().replace(/\W+/g, '-')}`}
                    className={`p-4 text-[14px] border transition-colors ${on ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-[#111] border-black/15 hover:border-[#C7A36A]'}`}
                  >{u}</button>
                );
              })}
            </div>
            <div className="mt-4 text-[12px] text-black/50">Multiple selection. Utilities you already have will be excluded from site development estimates.</div>
          </StepBlock>
        )}

        {step === 7 && (
          <StepBlock title="When do you plan to start?" testid="q-timeline">
            <div className="grid gap-3">
              {TIMELINES.map((t) => (
                <OptCard key={t} selected={data.timeline === t} onClick={() => set({ timeline: t })} testid={`q7-${t.toLowerCase().replace(/\W+/g, '-')}`}>{t}</OptCard>
              ))}
            </div>
          </StepBlock>
        )}

        {step === 8 && (
          <StepBlock title="Where should we send your feasibility report?" testid="q-contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Full name"><TextInput value={data.full_name} onChange={(v) => set({ full_name: v })} testid="q8-name" placeholder="Your name" /></Field>
              <Field label="Email"><TextInput type="email" value={data.email} onChange={(v) => set({ email: v })} testid="q8-email" placeholder="name@company.com" /></Field>
              <Field label="Phone"><TextInput value={data.phone} onChange={(v) => set({ phone: v })} testid="q8-phone" placeholder="+91 …" /></Field>
            </div>
            <div className="mt-6 text-[12px] text-black/50">We&apos;ll email you a copy of your report and store your assumptions so you can revise them later.</div>
          </StepBlock>
        )}

        {/* Nav */}
        <div className="mt-10 pt-6 border-t border-black/10 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={back}
            disabled={step === 1}
            data-testid="q-back"
            className="text-[11px] tracking-[0.22em] uppercase text-black/60 hover:text-[#C7A36A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >← Back</button>
          <button
            type="button"
            onClick={next}
            disabled={!canProceed}
            data-testid="q-next"
            className="rounded-full bg-[#111] text-white px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >{step === total ? 'See my report →' : 'Continue →'}</button>
        </div>
      </div>
    </div>
  );
}

const StepBlock = ({ title, children, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-3">Assessment</div>
    <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05] mb-8">{title}</h3>
    {children}
  </div>
);

export function clearFeasibilityDraft() {
  try { localStorage.removeItem(AUTOSAVE_KEY); } catch (e) { /* ignore */ }
}
